import React from "react";
import { motion } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const services = [
  {
    icon: "☁️",
    iconBg: "rgba(26,107,255,.15)",
    iconBorder: "rgba(26,107,255,.25)",
    title: "Cloud Platform",
    description:
      "Enterprise-grade cloud infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee. Deploy in minutes, not months.",
    tags: ["Auto-Scaling", "Global CDN", "Serverless", "Multi-Cloud"],
    tagStyle: { background: "rgba(26,107,255,.12)", color: "#7eb8ff", border: "1px solid rgba(26,107,255,.2)" },
  },
  {
    icon: "📊",
    iconBg: "rgba(0,180,130,.1)",
    iconBorder: "rgba(0,180,130,.2)",
    title: "Data & Analytics",
    description:
      "Real-time data processing, AI-powered insights, and customizable dashboards. Turn your data into competitive advantage instantly.",
    tags: ["Real-Time Analytics", "ML Insights", "Data Pipeline", "Custom Reports"],
    tagStyle: { background: "rgba(0,180,130,.1)", color: "#5de0c0", border: "1px solid rgba(0,180,130,.2)" },
  },
  {
    icon: "🤖",
    iconBg: "rgba(120,70,220,.1)",
    iconBorder: "rgba(120,70,220,.2)",
    title: "AI & Automation",
    description:
      "Pre-built AI models, workflow automation, and intelligent APIs. Accelerate innovation without the complexity of building from scratch.",
    tags: ["Pre-trained Models", "Workflow Automation", "API Access", "Custom AI"],
    tagStyle: { background: "rgba(120,70,220,.1)", color: "#b48aff", border: "1px solid rgba(120,70,220,.2)" },
  },
  {
    icon: "⚡",
    iconBg: "rgba(240,130,0,.1)",
    iconBorder: "rgba(240,130,0,.2)",
    title: "Integration Hub",
    description:
      "Connect 500+ tools seamlessly with our integration marketplace. One platform to unify your entire technology stack.",
    tags: ["API Marketplace", "Webhooks", "Custom Connectors", "Zapier Support"],
    tagStyle: { background: "rgba(240,130,0,.1)", color: "#ffb055", border: "1px solid rgba(240,130,0,.2)" },
  },
];

const Services = () => {
  return (
    <section
      id="services"
      className="section-padding"
      style={{ background: "var(--navy2)" }}
      aria-labelledby="services-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Platform Features"
          title="Everything You Need in<br/>One Unified Platform"
          subtitle="Powerful features that work together seamlessly. From startups to enterprises, our platform scales with your ambition."
        />

        <motion.div 
          className="grid gap-6 mt-12" 
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              className="p-0"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                delay: i * 0.15,
                type: "spring",
                stiffness: 100,
                damping: 15
              }}
              whileHover={{ 
                y: -8,
                scale: 1.02,
                transition: { duration: 0.3, type: "spring", stiffness: 400 }
              }}
            >
              <div className="p-8">
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-5"
                  style={{ background: s.iconBg, border: `1px solid ${s.iconBorder}` }}
                  aria-hidden="true"
                >
                  {s.icon}
                </div>

                <h3
                  className="font-display font-bold text-white text-lg mb-3"
                  id={`service-${i}`}
                >
                  {s.title}
                </h3>
                <p className="text-sm leading-7 mb-4" style={{ color: "var(--slate)" }}>
                  {s.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2" role="list" aria-label={`${s.title} technologies`}>
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      role="listitem"
                      className="text-xs font-medium px-3 py-1 rounded-full"
                      style={s.tagStyle}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
